import axios from "axios";
import { toast } from "react-toastify";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_BASE_URL,
});

// List of public endpoints that don't require authentication
const publicEndpoints = [
  '/gpstracker/usersessionhandler/', // Login endpoint
  '/gpstracker/forgotpasswordhandler/', // Forgot password endpoints (POST & PUT)
];

// Helper function to check if endpoint is public
const isPublicEndpoint = (url: string) => {
  return publicEndpoints.some(endpoint => url.includes(endpoint));
};

axiosInstance.interceptors.request.use(
  (config) => {
    // Check if this is a public endpoint
    const isPublic = isPublicEndpoint(config.url || '');

    if (!isPublic) {
      // Check if user is authenticated
      const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const hasToken = userData && userData['X-AUTHORIZATION-TOKEN'];

      if (!isAuthenticated || !hasToken) {
        // Clear any stale auth data
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        // Redirect to login page if not already there
        if (window.location.pathname !== '/login' && 
            window.location.pathname !== '/signup' && 
            window.location.pathname !== '/forgot-password') {
          window.location.href = '/login';
        }
        // Cancel the request by throwing an error
        return Promise.reject(new Error('User is not authenticated'));
      }

      // Add authorization token if authenticated
      if (userData && userData['X-AUTHORIZATION-TOKEN']) {
        config.headers['X-AUTHORIZATION-TOKEN'] = userData['X-AUTHORIZATION-TOKEN'];
      }
    } else {
      // For public endpoints, still try to add token if available (optional)
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData && userData['X-AUTHORIZATION-TOKEN']) {
        config.headers['X-AUTHORIZATION-TOKEN'] = userData['X-AUTHORIZATION-TOKEN'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors from request interceptor
    if (error.message === 'User is not authenticated') {
      return Promise.reject(error);
    }

    const { response } = error;
    if (response) {
      if (response.status === 401) {
        console.error("Unauthorized access. Please log in.");
        // Clear authentication data
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        // Redirect to login page if not already there
        if (window.location.pathname !== '/login' && 
            window.location.pathname !== '/signup' && 
            window.location.pathname !== '/forgot-password') {
          window.location.href = '/login';
        }
        toast.error("Session expired. Please login again.");
        return Promise.reject(response.data);
      }
      const errorMessage = response.data?.message || "An error occurred";
      toast.error(errorMessage);
      return Promise.reject(response.data);
    } else {
      // Handle network errors or other errors
      if (error.message !== 'User is not authenticated') {
        console.error("Network Error:", error.message);
      }
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
